/**
 * usePaymentMutation — Sprint 29
 * TanStack Query mutation per actualitzar l'estat de pagament d'una reserva.
 * Crida PATCH /reservations/:id/payment i invalida la cache del calendari.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

// ============================================
// TYPES
// ============================================

export type PaymentStatus = 'PENDING' | 'PAID' | 'PARTIAL' | 'REFUNDED';
export type PaymentMethod = 'CASH' | 'CARD' | 'TRANSFER' | 'STRIPE' | 'BOOKING_OTA' | 'EXPEDIA_OTA';

export interface UpdatePaymentPayload {
  status: PaymentStatus;
  method?: PaymentMethod;
  notes?: string;
}

export interface UpdatePaymentVariables {
  reservationId: string;
  payload: UpdatePaymentPayload;
}

// ============================================
// HOOK
// ============================================

/**
 * Mutation per marcar el pagament d'una reserva.
 *
 * @example
 * const { mutate, isPending } = usePaymentMutation();
 * mutate({ reservationId: '123', payload: { status: 'PAID', method: 'CASH' } });
 */
export function usePaymentMutation() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdatePaymentVariables>({
    mutationFn: async ({ reservationId, payload }) => {
      await api.patch(`/reservations/${reservationId}/payment`, payload);
    },
    onSuccess: () => {
      // Invalida totes les queries del calendari per refrescar les dades
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'] });
    },
  });
}
